/**
 * measurement-display — the ONE resolved-measurement formatter (T36.3 / ADR-015).
 *
 * Every surface (catalogue, compact, expanded, settings) renders a
 * HouseholdProduct's measurement through THIS function, so `4 L` and `12 × 20 g`
 * read the same everywhere — no double-render, no metric/imperial flag, no
 * offer-direct unit.
 *
 * Pure + portable (CP1: no platform imports). It reads ONLY the resolved
 * measurement snapshot physically on the synced household row (count / value /
 * unit / dimension + the dimension's `forms` for offline unit-switching). It
 * never converts: a weight/volume unit switch reads a different stored `forms`
 * value; conversion math lives only in the packing normalizer + the backend
 * writer.
 *
 * No fabricated unit: a HouseholdProduct with no resolved measurement (no linked
 * SystemProduct / no offer-sourced packing) formats to null — the caller renders
 * nothing, never `ea`.
 */

/**
 * The snapshot fields this formatter reads (a subset of HouseholdProduct). Fields
 * explicitly include `undefined` so a `HouseholdProductWithDetails` (whose
 * snapshot keys are optional) passes under `exactOptionalPropertyTypes`.
 */
export interface MeasurementSnapshotFields {
  measurement_pack_count?: number | null | undefined;
  measurement_value?: number | null | undefined;
  measurement_unit?: string | null | undefined;
  measurement_dimension?: string | null | undefined;
  measurement_forms?: Record<string, number> | null | undefined;
}

/** Round a form value to a tidy display number (max 3 dp, no trailing zeros). */
function tidy(n: number): string {
  // `4` not `4.000`; `0.454` not `0.45400000000000001`.
  const rounded = Math.round(n * 1000) / 1000;
  return String(rounded);
}

/**
 * Has the product a resolved measurement at all? False when the snapshot is
 * empty (no unit) — the caller then renders no measurement (no `ea`).
 */
export function hasResolvedMeasurement(p: MeasurementSnapshotFields): boolean {
  return (p.measurement_unit ?? '').trim() !== '';
}

/**
 * Format the resolved measurement, optionally in a chosen display unit (weight /
 * volume only — a different stored `forms` value, read not computed). Returns null
 * when there is no resolved measurement.
 *
 *   - Multipack (`pack_count > 1`):       `12 × 20 g`
 *   - Sized single (`value`, `pack_count` 1/absent): `4 L`
 *   - Loose (unit, no value):             `lb`
 *
 * @param displayUnit a weight/volume unit to render in (kg → lb switch). Ignored
 *   when absent from the snapshot's `forms` (e.g. a count product, or a form not
 *   stored) — falls back to the stored `measurement_unit` + `measurement_value`.
 */
export function formatResolvedMeasurement(
  p: MeasurementSnapshotFields,
  displayUnit?: string | null,
): string | null {
  const unit = (p.measurement_unit ?? '').trim();
  if (unit === '') return null;

  const packCount = p.measurement_pack_count ?? 1;
  const forms = p.measurement_forms ?? {};

  // Resolve the value + unit to render. A requested displayUnit that the snapshot
  // stores a form for wins (offline unit switch); otherwise the stored form.
  let renderUnit = unit;
  let renderValue = p.measurement_value ?? null;
  if (displayUnit != null && displayUnit !== unit) {
    const formValue = forms[displayUnit];
    if (formValue !== undefined) {
      renderUnit = displayUnit;
      renderValue = formValue;
    }
  }

  // Loose product — unit only, no value (bananas `lb`).
  if (renderValue == null) {
    return packCount > 1 ? `${packCount} × ${renderUnit}` : renderUnit;
  }

  const sized = `${tidy(renderValue)} ${renderUnit}`;
  return packCount > 1 ? `${packCount} × ${sized}` : sized;
}

/**
 * The display units a weight/volume snapshot can switch between (the keys of its
 * stored `forms`). Empty for a count / loose / unit-less snapshot — the caller
 * then offers no unit switch. Order is the snapshot's stored form order.
 */
export function availableDisplayUnits(p: MeasurementSnapshotFields): string[] {
  const dim = p.measurement_dimension;
  if (dim !== 'weight' && dim !== 'volume') return [];
  const forms = p.measurement_forms ?? {};
  return Object.keys(forms);
}
